'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { LegacyTablerIconSize } from '#pplx/frontend/aether/primitives/IconPrimitive/iconSizing.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { ComposerContainerContext, COVERED_CHIN_HEIGHT_PROPERTY, } from '#pplx/frontend/composer-sdk/ComposerContainerContext.js';
import { composerShadowGeometry } from '#pplx/frontend/composer-sdk/internal/shadowGeometry.js';
import { getComposerSurfaceClassNames } from '#pplx/frontend/composer-sdk/internal/surfaceStyles.js';
import { useComposerFocusRing } from '#pplx/frontend/composer-sdk/internal/useComposerFocusRing.js';
import { decorativeGlowStyles, framedComposerGeometry, getComposerMaskClassNames, getBoxAppearanceStyles, composerStyles, isFramedAppearance, } from '#pplx/frontend/composer-sdk/styles.js';
import { isInteractiveElement } from '#pplx/frontend/dom/isInteractiveElement.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { styles as containerStyles } from '#pplx_generated/pplx/frontend/composer-sdk/ComposerContainer.module.css.js';
import { styles as surfaceStyles } from '#pplx_generated/pplx/frontend/composer-sdk/ComposerSurface.module.css.js';
import classNames from 'classnames';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
function getContainerShadowProps(useShadowOverlay, attachedDropdownPlacement, isActive) {
    if (!useShadowOverlay)
        return {};
    return {
        shadowSize: attachedDropdownPlacement ? 'md' : 'sm',
        shadowFocused: !!attachedDropdownPlacement || isActive,
        shadowLayer: 'overlay',
    };
}
function getInputShadowProps(hasTokenShadow, useShadowOverlay, shadowElevation, isActive) {
    if (!hasTokenShadow || useShadowOverlay || shadowElevation === 'xl')
        return {};
    return { shadowSize: 'sm', shadowFocused: isActive };
}
// Hosts anchor attached suggestions below the frame and tuck them under its bottom inset. With a
// chin, the input ends above that edge by the chin row's height, so measure the remaining gap.
function useCoveredChinHeight({ enabled, frameRef, inputBoxRef, }) {
    const [heightPx, setHeightPx] = useState(0);
    useLayoutEffect(() => {
        const frame = frameRef.current;
        const inputBox = inputBoxRef.current;
        if (!enabled || frame === null || inputBox === null) {
            setHeightPx(0);
            return;
        }
        const measure = () => {
            const gapPx = frame.getBoundingClientRect().bottom -
                inputBox.getBoundingClientRect().bottom -
                framedComposerGeometry.insetPx;
            setHeightPx(Math.max(0, Math.round(gapPx)));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(frame);
        observer.observe(inputBox);
        return () => observer.disconnect();
    }, [enabled, frameRef, inputBoxRef]);
    return heightPx;
}
/** Presentational composer container shared by composer-sdk and ResizableInput. */
export const ComposerContainer = React.memo(function ComposerContainer({ wrapperClass, dashed, appearance = 'raised', mask, hasShadow, shadowElevation, decorativeGlow, onDecorativeGlowChange, attachedDropdownPlacement: dropdownPlacement, attachedDropdownKind = 'suggestions', suggestionAttachment = 'input', framedCommandMenuOpen, framedChinVisible, isMobileUserAgent, quote, quotePreview, setQuote, attachmentsList, inputWarnings, topContent, inputBoxOverlay, attachedHeader, bottomContent, children, inputRef, inputContainerRef, ref, isActive, isExpanded, }) {
    const inputBoxElementRef = useRef(null);
    const inputBoxRef = useRef({
        getBoundingClientRect: () => inputBoxElementRef.current?.getBoundingClientRect(),
    });
    const isFocusRingVisible = useComposerFocusRing({ isActive });
    const hasBottomContent = Boolean(bottomContent);
    const isFramed = isFramedAppearance(appearance);
    const attachSuggestionsToFrame = isFramed && suggestionAttachment === 'frame';
    const attachedDropdownPlacement = attachSuggestionsToFrame ? undefined : dropdownPlacement;
    const isDropdownAbove = attachedDropdownPlacement === 'top';
    const isDropdownBelow = attachedDropdownPlacement === 'bottom';
    const isAccentFrame = appearance === 'framed-accent';
    const hasMask = !!mask;
    const maskClassNames = mask && getComposerMaskClassNames(mask);
    const hasMaskedFill = hasMask && (appearance === 'raised' || appearance === 'flat');
    const hasFramedHeader = isFramed && attachedHeader !== undefined && attachedHeader !== null;
    const hasFramedBottomRow = framedCommandMenuOpen || (framedChinVisible ?? hasBottomContent);
    // Suggestions below the input join its bottom edge, so they cover a visible chin. The frame
    // stops painting that row and hides it until the suggestions close.
    const coversFramedChin = isFramed &&
        isDropdownBelow &&
        attachedDropdownKind === 'suggestions' &&
        !framedCommandMenuOpen &&
        hasFramedBottomRow;
    const paintsFramedBottomRow = hasFramedBottomRow && !coversFramedChin;
    const paintsFramedBackground = paintsFramedBottomRow ||
        (attachSuggestionsToFrame &&
            dropdownPlacement === 'top' &&
            attachedDropdownKind === 'suggestions');
    const ownFrameRef = useRef(null);
    const frameRef = ref ?? ownFrameRef;
    const coveredChinHeightPx = useCoveredChinHeight({
        enabled: coversFramedChin,
        frameRef,
        inputBoxRef: inputBoxElementRef,
    });
    const framedRadiusClassName = isFramed &&
        classNames(framedComposerGeometry.frameRadiusClassName, hasFramedBottomRow &&
            dropdownPlacement !== 'bottom' &&
            framedComposerGeometry.commandMenuBottomRadiusClassName, dropdownPlacement !== 'top' && 'has-[[data-framed-header]:not(:empty)]:rounded-t-[20px]');
    const hasTokenShadow = !isFramed && appearance === 'raised' && !!hasShadow && !quote && !decorativeGlow && !dashed;
    const useShadowOverlay = hasTokenShadow &&
        (hasBottomContent || attachedDropdownPlacement !== undefined) &&
        shadowElevation !== 'xl';
    const inputClass = useMemo(() => {
        if (isFramedAppearance(appearance)) {
            return classNames('w-full', containerStyles.frame, attachSuggestionsToFrame && containerStyles.frameAttachment, framedRadiusClassName, framedComposerGeometry.paddingClassName, 
            // Recessed tokens are translucent; `mask` supplies the opaque surface beneath them.
            // Paint the frame for its rows or for suggestions joined above the input.
            isAccentFrame
                ? paintsFramedBackground
                    ? 'bg-super-recessed'
                    : 'bg-transparent has-[[data-framed-header]:not(:empty)]:bg-super-recessed'
                : paintsFramedBackground
                    ? 'bg-recessed'
                    : 'bg-transparent has-[[data-framed-header]:not(:empty)]:bg-recessed', 
            // Hide the frame at once so its edge never fades out beside the opening suggestions.
            coversFramedChin && containerStyles.frameCovered, wrapperClass, 
            // Extend the blur past the join before clipping to avoid a seam.
            attachSuggestionsToFrame &&
                dropdownPlacement !== undefined && [
                'after:pointer-events-none after:absolute after:inset-0 after:rounded-inherit after:content-[""] forced-colors:after:hidden',
                // Full composer shadow tokens include a keyline; the frame needs blur only.
                'after:shadow-subtle',
                dropdownPlacement === 'top'
                    ? composerShadowGeometry.extendTop
                    : composerShadowGeometry.extendBottom,
            ], {
                'rounded-b-none': dropdownPlacement === 'bottom',
                'rounded-t-none': dropdownPlacement === 'top',
            });
        }
        const appearanceClasses = getBoxAppearanceStyles({
            appearance,
            isActive,
            hasTokenShadow,
        });
        return classNames(...composerStyles.outerWrapper.base, composerStyles.outerWrapper.transition, wrapperClass, appearanceClasses, hasMaskedFill && (hasBottomContent ? 'dark:bg-transparent' : 'dark:bg-composer'), dashed && 'border-dashed !shadow-none', dashed && !decorativeGlow && '!border-base', 
        // Clip the token's keyline and drop shadow at the join, preserving the
        // dropdown's divider instead of drawing another edge through the surface.
        hasTokenShadow && isDropdownBelow && composerShadowGeometry.clipBottom, hasTokenShadow && isDropdownAbove && composerShadowGeometry.clipTop, !isFramed && decorativeGlow && decorativeGlowStyles.border, !hasBottomContent && (isExpanded ? 'pt-3' : 'pt-2'), hasBottomContent &&
            !useShadowOverlay &&
            (hasMaskedFill ? '!bg-subtle dark:!bg-transparent' : '!bg-subtle dark:!bg-base'), 
        // Square the visible box on the edge attached to an open dropdown and drop its
        // joined border, so the box and the dropdown read as one contiguous shape.
        {
            'rounded-b-none border-b-0 forced-colors:border-b-0': isDropdownBelow,
            'rounded-t-none rounded-b-2xl border-t-0 forced-colors:border-t-0': isDropdownAbove,
        });
    }, [
        isActive,
        wrapperClass,
        appearance,
        dashed,
        hasTokenShadow,
        decorativeGlow,
        isExpanded,
        hasBottomContent,
        hasMaskedFill,
        useShadowOverlay,
        isFramed,
        isAccentFrame,
        paintsFramedBackground,
        coversFramedChin,
        framedRadiusClassName,
        isDropdownAbove,
        isDropdownBelow,
        dropdownPlacement,
        attachSuggestionsToFrame,
    ]);
    const handleClick = useCallback((event) => {
        if (isMobileUserAgent)
            return;
        const target = event.target;
        if (target.closest('[data-framed-header]') || isInteractiveElement(target)) {
            return;
        }
        inputRef?.current?.focus?.();
    }, [inputRef, isMobileUserAgent]);
    const handleMouseDown = useCallback((event) => {
        if (isMobileUserAgent)
            return;
        const target = event.target;
        if (target.closest('[data-framed-header]') || isInteractiveElement(target)) {
            return;
        }
        // If not clicking on the input element we want to prevent the default, otherwise the input
        // will be blurred before immediately being focused again. This would cause any suggestions
        // dropdown to be closed then immediately reopened. However, we cannot prevent the default
        // always, otherwise the input's default behaviour--positioning the cursor where you clicked
        // in the text--will also be prevented.
        const inputElement = inputRef?.current?.element();
        if (inputElement && target !== inputElement && !inputElement.contains(target)) {
            // Prevent default mousedown behavior, keeping focus in input
            event.preventDefault();
        }
    }, [inputRef, isMobileUserAgent]);
    const onClear = useCallback(() => setQuote?.(null), [setQuote]);
    const containerContextValue = useMemo(() => ({
        hasMask,
        suggestionAppearance: isFramed && !attachSuggestionsToFrame ? appearance : undefined,
        isSuggestionAttachedToFrame: attachSuggestionsToFrame,
        inputAppearance: appearance,
        hasTokenShadow,
        decorativeGlow,
        setDecorativeGlow: onDecorativeGlowChange,
        isFramed,
        isInsideFramedHeader: false,
        // Without a separate input surface, overlays use the existing container anchor.
        inputBoxRef: isFramed || hasBottomContent ? inputBoxRef : undefined,
        coveredChinHeightPx,
    }), [
        coveredChinHeightPx,
        decorativeGlow,
        onDecorativeGlowChange,
        isFramed,
        hasBottomContent,
        hasMask,
        attachSuggestionsToFrame,
        appearance,
        hasTokenShadow,
    ]);
    // Only provided when `hasFramedHeader`, which requires a framed container.
    const framedHeaderContextValue = useMemo(() => ({
        ...containerContextValue,
        isFramed: true,
        isInsideFramedHeader: true,
    }), [containerContextValue]);
    const mainContent = (_jsxs(_Fragment, { children: [topContent, (inputWarnings || quote || attachmentsList) && (_jsxs("div", { className: classNames({ 'pt-1': !isExpanded }, 'empty:hidden gap-y-2 grid items-center'), children: [inputWarnings, quote && (quotePreview ?? _jsx(QuotePreview, { quote: quote, onClear: onClear })), attachmentsList] })), _jsx("div", { className: classNames('min-w-0 px-3', 
                // The framed editor can't clip (its shadow layer would be cut), so clip the row's
                // sides instead. Narrow hosts then cut actions at the input edge. Only the x-axis:
                // corner or y clipping would shave round actions that sit inside the 20px radius.
                isFramed && 'overflow-x-clip', isExpanded
                    ? 'grid grid-cols-[1fr_auto] grid-rows-1fr-auto pb-3'
                    : 'flex flex-nowrap pb-2'), children: children })] }));
    return (_jsx(ComposerContainerContext.Provider, { value: containerContextValue, children: _jsxs(BoxPrimitive, { noBorder: true, ...getContainerShadowProps(useShadowOverlay, attachedDropdownPlacement, isActive), className: classNames(composerShadowGeometry.overhang, 'relative isolate', getComposerSurfaceClassNames({ appearance, mask, suggestionAttachment }), isFramed
                ? [
                    containerStyles.frame,
                    framedRadiusClassName,
                    // The frame bleeds past the host column so the editor, not the frame, aligns
                    // with sibling content. Hosts anchoring overlays to this box's parent must
                    // account for the bleed (see `framedComposerGeometry`).
                    framedComposerGeometry.editorAlignmentClassName,
                ]
                : 'rounded-2xl', maskClassNames, 
            // Preserve the light frame's base backing unless the host supplies its own mask.
            isFramed &&
                !hasMask && [
                paintsFramedBackground
                    ? 'bg-base'
                    : 'has-[[data-framed-header]:not(:empty)]:bg-base',
                'dark:!bg-transparent',
            ], hasTokenShadow &&
                isFocusRingVisible &&
                // Hosts may set --composer-focus-ring-offset to an inherited CSS length (default 2px).
                // Scope it to the closest composer host so other containers keep the default.
                // A value of 0px joins this unframed ring to the opaque input; framed rings ignore it.
                'outline outline-2 outline-offset-[var(--composer-focus-ring-offset,2px)] outline-super-strong forced-colors:outline-[CanvasText]', 
            // Forced colors can reveal the editor's transparent native outline inside the box;
            // the outer CanvasText ring above is the visible focus indicator for this surface.
            hasTokenShadow && 'forced-colors:[&_[contenteditable]]:outline-0', 
            // Keep the floating shadow outside the footer's overflow clip. Extend it past
            // attached edges so the inset keyline cannot double the dropdown divider.
            hasTokenShadow &&
                shadowElevation === 'xl' && [
                'after:pointer-events-none after:absolute after:inset-0 after:z-[2] after:rounded-inherit after:shadow-lg after:content-[""] forced-colors:after:hidden',
                isDropdownBelow && composerShadowGeometry.extendBottom,
                isDropdownAbove && composerShadowGeometry.extendTop,
            ], 
            // Attached suggestions use focused md on both halves so the join keeps
            // the same shadow depth.
            useShadowOverlay && isDropdownBelow && composerShadowGeometry.extendBottom, useShadowOverlay && isDropdownAbove && composerShadowGeometry.extendTop, {
                'rounded-b-none': dropdownPlacement === 'bottom',
                'rounded-t-none': dropdownPlacement === 'top',
            }), ref: frameRef, 
            // Portaled suggestions inherit this offset to rise over the covered chin.
            style: coveredChinHeightPx > 0
                ? {
                    [COVERED_CHIN_HEIGHT_PROPERTY]: `${coveredChinHeightPx}px`,
                }
                : undefined, onClick: handleClick, onMouseDownCapture: handleMouseDown, children: [_jsxs(BoxPrimitive, { noBorder: true, ...getInputShadowProps(hasTokenShadow, useShadowOverlay, shadowElevation, isActive), ref: inputContainerRef, "data-appearance": appearance, className: classNames(inputClass, 'relative z-[1] grid items-center', 
                    // Bottom content participates in the same grid as the input row, so
                    // collapse the generic row gap and use margins to keep the footer
                    // attached to the container edge.
                    hasBottomContent
                        ? classNames(!isFramed && 'overflow-hidden', '[&>*+*]:mt-4 [&>*:last-child]:mt-0', hasFramedHeader && '[&>*:nth-child(2)]:mt-0')
                        : hasFramedHeader
                            ? 'gap-y-0'
                            : 'gap-y-4'), children: [hasFramedHeader && (_jsx(ComposerContainerContext.Provider, { value: framedHeaderContextValue, children: _jsx("div", { "data-framed-header": true, className: "relative -ms-1.5 z-[1] mb-1.5 min-w-0 empty:hidden", children: attachedHeader }) })), _jsxs(BoxPrimitive, { ref: inputBoxElementRef, noBorder: true, className: classNames(!hasBottomContent &&
                                !isFramed &&
                                (hasTokenShadow ? 'relative z-[1] grid gap-y-4' : 'contents'), hasBottomContent &&
                                !isFramed && [
                                'relative z-[1] grid bg-raised',
                                // Only the editor tints the mask; the surrounding footer stays transparent.
                                hasMaskedFill && 'dark:bg-composer',
                                // The shadow token or dashed outer edge already owns the footer's perimeter.
                                !useShadowOverlay && !dashed && 'ring-1 ring-soft',
                                isExpanded ? 'pt-3' : 'pt-2',
                                'gap-4',
                                {
                                    'rounded-b-none': isDropdownBelow,
                                    'rounded-b-2xl': !isDropdownBelow,
                                },
                            ], isFramed && [
                                'relative z-[1] grid gap-4 rounded-[20px] forced-colors:border forced-colors:border-[CanvasText]',
                                isAccentFrame ? 'bg-composer-accent' : 'bg-composer',
                                containerStyles.editor,
                                surfaceStyles.fill,
                                surfaceStyles.shadow,
                                isAccentFrame && surfaceStyles.accent,
                                // Keep one shadow layer across suggestion visibility changes so the
                                // old perimeter cannot fade out over a newly mounted perimeter.
                                [
                                    !isAccentFrame &&
                                        (isActive ? 'after:shadow-composer-focus' : 'after:shadow-composer'),
                                    isAccentFrame && attachSuggestionsToFrame && 'after:shadow-sm-accent',
                                    isAccentFrame &&
                                        !attachSuggestionsToFrame &&
                                        (isActive
                                            ? 'after:shadow-composer-accent-focus'
                                            : 'after:shadow-composer-accent'),
                                    // Suggestions own the divider; extend and clip the attached edge.
                                    isDropdownBelow && composerShadowGeometry.extendBottom,
                                    isDropdownAbove && composerShadowGeometry.extendTop,
                                    // Draw the dashed edge on the absolutely positioned shadow layer so
                                    // toggling it never changes the editor's size.
                                    dashed &&
                                        'after:!shadow-none after:border after:border-dashed after:border-heavy',
                                ],
                                isFocusRingVisible &&
                                    'outline outline-2 outline-offset-2 outline-super-strong forced-colors:outline-[CanvasText]',
                                'forced-colors:[&_[contenteditable]]:outline-0',
                                isExpanded ? 'pt-3' : 'pt-2',
                                {
                                    'rounded-b-none': isDropdownBelow,
                                    'rounded-t-none': isDropdownAbove,
                                },
                            ], 
                            // Forced-colors mode hides the shadow layer and draws the edge on this box instead.
                            isFramed && dashed && 'forced-colors:border-dashed', 
                            // Reserve the missing border inside the shadow edge for every row of mainContent.
                            // A border on the shadow-bearing box separates its inset keyline from the drop shadow.
                            // Forced-colors mode draws the edge on the outer box, so the spacers must not draw again.
                            hasTokenShadow && 'border-x border-transparent forced-colors:border-0', hasTokenShadow && !isDropdownAbove && 'border-t', hasTokenShadow && !hasBottomContent && !isDropdownBelow && 'border-b'), children: [mainContent, isFramed && inputBoxOverlay] }), hasBottomContent &&
                            (hasTokenShadow ? (_jsx("div", { className: classNames(
                                // The overlay needs a footer tint; xl already tints the containing box.
                                useShadowOverlay &&
                                    (hasMaskedFill ? 'bg-subtle dark:bg-transparent' : 'bg-subtle dark:bg-base'), 'border-x border-transparent forced-colors:border-0', !isDropdownBelow && 'border-b'), children: bottomContent })) : isFramed ? (_jsx("div", { className: classNames('contents', coversFramedChin && 'invisible'), 
                                // Scope interaction colors to the chin and embedded menus. The editor
                                // and floating overlays keep their own surface palette.
                                style: isAccentFrame
                                    ? {
                                        '--bg-soft': 'var(--accent-bg-soft)',
                                        '--bg-subtle': 'var(--accent-bg-subtle)',
                                    }
                                    : undefined, children: bottomContent })) : (bottomContent))] }), !isFramed && decorativeGlow !== undefined && (!dashed || decorativeGlow) && (_jsx("div", { "aria-hidden": true, className: classNames('pointer-events-none absolute inset-px z-0', decorativeGlow ? decorativeGlowStyles.shadow : decorativeGlowStyles.idleShadow, {
                        'rounded-[15px]': attachedDropdownPlacement === undefined,
                        'rounded-b-none rounded-t-[15px]': isDropdownBelow,
                        'rounded-b-[15px] rounded-t-none': isDropdownAbove,
                        [decorativeGlowStyles.clipBottom]: isDropdownBelow,
                        [decorativeGlowStyles.clipTop]: isDropdownAbove,
                    }) }))] }) }));
});
const QuotePreview = React.memo(function QuotePreview({ quote, onClear, }) {
    const { $t } = useIntl();
    if (!quote)
        return null;
    const clearQuoteLabel = $t({
        defaultMessage: 'Clear quote',
        description: 'Accessible label and tooltip for the button that clears the quoted text preview',
        id: '8d02aedc1d',
    });
    return (_jsx("div", { className: "py-2 bg-subtle dark:bg-base animate-slideDownAndFadeIn z-[49] mx-3 rounded-lg px-3", children: _jsxs("div", { className: "gap-2 flex items-start", children: [_jsx(TextPrimitive, { color: "light", className: "mt-px flex items-center pt-1", variant: "tinyRegular", children: _jsx(IconPrimitive, { icon: $icon('quote-filled'), size: LegacyTablerIconSize.xs, className: "rotate-180" }) }), _jsx(TextPrimitive, { lineClamp: 3, className: "py-1 flex-1 overflow-hidden text-ellipsis", variant: "tinyRegular", color: "light", children: quote }), _jsx("div", { className: "shrink-0", children: _jsx(Button, { "aria-label": clearQuoteLabel, icon: $icon('x'), onClick: onClear, rounded: true, size: "tiny", variant: "text" }) })] }) }));
});
//# sourceMappingURL=ComposerContainer.js.map